import {
  Button,
  Drawer,
  Form,
  getValueFromEvent,
  Grid,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
  Upload,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { EPaymentType } from 'enums';
import { EUserType } from 'pages/accounts/logs.type';
import { useEffect, useState } from 'react';
import { RiArticleLine } from 'react-icons/ri';
import { getAllCompanyConfig } from 'services/config';
import { getPaymentMethods, getPaymentReceipt } from 'services/payment';
import {
  beforeReceiptUploadValidation,
  isLessThanSize,
  isPurchaseReceiptFile,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import { previewPdfFromBase64 } from 'utilities/shared';
import styles from './style.module.css';

interface IPaymentMethodOption {
  id: string;
  name: string;
}

export interface IPaymentFormValues {
  attachments: any[];
  paymentType: EPaymentType;
  paymentMethod: string;
  companyBank: string;
  paymentDetails: string;
  currentPaidAmount: number;
}

type MakePaymentProps = {
  paymentBy: 'client' | 'supplier';
  paymentById: string;
  totalPayableAmount: number;
  visible: boolean;
  loading: boolean;
  defaultAmount?: number;
  closeDrawer: () => void;
  onPaymentSubmit: (data: IPaymentFormValues) => Promise<any>;
};

const { Option } = Select;

export const MakePaymentWithReceipt: React.FC<MakePaymentProps> = (props) => {
  const [isPaymentMethodDisable, setIsPaymentMethodDisable] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [companyPaymentMethods, setCompanyPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [ledgerId, setLedgerId] = useState<null | string>(null);
  const [isPaymentReceiptLoading, setIsPaymentReceiptLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const paymentTypes = [EPaymentType.Cash, EPaymentType.Cheque];

  const clientOrSupplierPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Paying Bank' : 'Receiving Bank';
  const companyPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Receiving Bank' : 'Paying Bank';

  useEffect(() => {
    if (!isPaymentMethodDisable) return;

    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const companyId = data?.[0]?.id;
        const [paymentMethodsResponse, companyPaymentMethodsResponse] = await Promise.all([
          getPaymentMethods(props.paymentBy, props.paymentById),
          getPaymentMethods('company', companyId),
        ]);

        const extendedPaymentMethods = paymentMethodsResponse.map((paymentMethod) => ({
          id: paymentMethod.id,
          name: paymentMethod.bankName,
        }));

        const extendedCompanyPaymentMethods = companyPaymentMethodsResponse.map(
          (paymentMethod) => ({
            id: paymentMethod.id,
            name: paymentMethod.bankName,
          })
        );

        setPaymentMethods(extendedPaymentMethods);
        setCompanyPaymentMethods(extendedCompanyPaymentMethods);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
        setPaymentMethods([]);
        setCompanyPaymentMethods([]);
      }
    })();
  }, [isPaymentMethodDisable, props.paymentBy, props.paymentById]);

  const onCloseButtonClick = () => {
    form.resetFields();
    props.closeDrawer();
    setLedgerId(null);
  };

  const getOptions = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const getOptionsCompany = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onPaymentTypeSelect = (value: EPaymentType) => {
    form.resetFields(['paymentMethod', 'companyBank']);
    if (value === EPaymentType.Cheque) {
      setIsPaymentMethodDisable(false);
    } else {
      setIsPaymentMethodDisable(true);
    }
  };

  const onFinish = async (values: IPaymentFormValues) => {
    const { currentPaidAmount, attachments } = values;
    const base64Files = [];

    if (currentPaidAmount === 0) {
      form.setFields([
        {
          name: 'currentPaidAmount',
          errors: ['Payment needs to be greater than 0'],
        },
      ]);
      return;
    }

    if (currentPaidAmount > Number.parseFloat(props.totalPayableAmount.toFixed(2))) {
      form.setFields([
        {
          name: 'currentPaidAmount',
          errors: [`Payment exceed total pending amount (${props.totalPayableAmount})`],
        },
      ]);
      return;
    }

    if (attachments && attachments.length) {
      for (const file of attachments) {
        const format = file.type || '';
        const size = file.size || 0;
        if (isPurchaseReceiptFile(format) && isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            base64Files.push({ ...rest, base64String });
          } else {
            base64Files.push(file);
          }
        }
      }
    }

    try {
      const id = await props.onPaymentSubmit({ ...values, attachments: base64Files });
      setLedgerId(id);
    } catch {
      onCloseButtonClick();
    }
  };

  const handlePrintReceipt = async () => {
    try {
      setIsPaymentReceiptLoading(true);
      if (!ledgerId) {
        throw new Error('Ledger ID not found');
      }

      const currentPaidAmount = form.getFieldValue('currentPaidAmount');
      const invoiceString = await getPaymentReceipt(
        ledgerId,
        props.paymentBy as EUserType,
        props.totalPayableAmount - currentPaidAmount
      );
      previewPdfFromBase64(invoiceString);
    } catch {
      notification.error({
        message: 'Failed',
        description: 'Failed to download payment receipt',
      });
    } finally {
      onCloseButtonClick();
      setIsPaymentReceiptLoading(false);
    }
  };

  return (
    <Drawer
      title="Add a new payment"
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={props.visible}
      onClose={onCloseButtonClick}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{ currentPaidAmount: props.defaultAmount }}
        disabled={!!ledgerId}
      >
        <Space direction="vertical" className={styles['file-upload']}>
          <Form.Item
            name="attachments"
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
            rules={[
              {
                required: false,
                message: 'Attachment is required',
              },
            ]}
          >
            <Upload.Dragger
              listType="picture"
              accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif, application/pdf"
              multiple
              maxCount={2}
              beforeUpload={beforeReceiptUploadValidation}
            >
              <p className="ant-upload-text">Drag & Drop</p>
              <p className="ant-upload-text">Image or PDF files in this area</p>
              <p className="ant-upload-hint">
                {`Files must be less than ${MAX_UPLOAD_FILE_SIZE_MB}MB and maximum file count is 2`}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Space>
        <Form.Item
          label="Payment Type"
          name="paymentType"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <Select
            showSearch
            placeholder="Select a type"
            onSelect={onPaymentTypeSelect}
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {paymentTypes.map((item) => (
              <Option key={item} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={clientOrSupplierPaymentMethodTitle}
          name="paymentMethod"
          rules={[{ required: false, message: 'Please select a method' }]}
        >
          <Select
            showSearch
            placeholder="Select a method"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={isPaymentMethodDisable}
          >
            {getOptions(paymentMethods)}
          </Select>
        </Form.Item>
        <Form.Item
          label={companyPaymentMethodTitle}
          name="companyBank"
          rules={[
            {
              required: true && !isPaymentMethodDisable,
              message: 'Please select a method',
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select a method"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={isPaymentMethodDisable}
          >
            {getOptionsCompany(companyPaymentMethods)}
          </Select>
        </Form.Item>
        <Form.Item label="Payment Details" name="paymentDetails">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          label="Payment Amount"
          name="currentPaidAmount"
          rules={[{ required: true, type: 'number' }]}
        >
          <InputNumber style={{ width: '100%' }} precision={2} />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 4, offset: 20 }} style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={props.loading} hidden={!!ledgerId}>
            Save
          </Button>
        </Form.Item>
      </Form>

      {!!ledgerId && (
        <Button
          block
          size="large"
          onClick={handlePrintReceipt}
          icon={<RiArticleLine size={18} />}
          loading={isPaymentReceiptLoading}
          disabled={isPaymentReceiptLoading}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <span> Print Receipt</span>
        </Button>
      )}
    </Drawer>
  );
};
